import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { keys } from '@clubhub/constants';
import {
  AffordabilityAndUiRequest,
  AffordabilityPollResponse,
  AffordabilityRequest,
  BdgAffordabilityRequest,
  BtlAffordabilityRequest,
  IUiAffordabilityModel,
  StepName,
} from '@clubhub/ignite/models/affordability';
import { ResultsView } from '@clubhub/ignite/models/affordability/enums/results-view';
import { CriteriaSteps } from '@clubhub/ignite/models/criteria/criteria-steps';
import { PropertyModelRequest } from '@clubhub/ignite/models/property';
import { CriteriaFormResult } from '@clubhub/ignite/pages/criteria-v2/models';
import {
  ProductSavedRequest,
  SavedFilters,
  SavedProductFilterState,
  SavedSearchOverviewViewModel,
  SavedSearchRerunViewModel,
} from '@clubhub/models/saved-searches';
import {
  CriteriaType,
  FlowType,
  LendingTypeCode,
  MiProductsFiltersViewModel,
  OutcomeDetails,
  ProductsBdgModelRequest,
  ProductsBtlModelRequest,
  ProductsResidentialModelRequest,
  PropertyOutcomeDetailsDetailed,
  SavedSearchRequest,
  SavedSearchResponse,
  SearchType,
} from '@shared/models';
import { CustomProductCalculations } from '@shared/models/custom-product-calculations';
import { ClubHubDataService } from '@shared/services/clubhub-data.service';
import { LendingTypeService } from '@shared/services/lending-type.service';
import { ResultsFilterService } from '@shared/services/results-filter.service';
import { ToastService } from '@shared/services/toast.service';
import { Observable, firstValueFrom, lastValueFrom, timer } from 'rxjs';
import { v4 as newGuid } from 'uuid';
import {
  CriteriaNavigateService,
  IgniteCommonDataService,
  IgniteService,
  ProductsResultSettingsService,
  ProductsSearchService,
  PropertySearchService,
} from '..';
import { AffordabilityService } from '../affordability/affordability.service';
import { CriteriaV2Service } from '../criteria/criteria-v2.service';
import { IgniteBtlService } from '../ignite-btl.service';
import { ProductsCalculationService } from '../products/products-calculation.service';
import { ProductsFilterService } from '../products/products-filter.service';
import { ProductsFilterHelper } from '../products/products-filter/products-filter.helper';
import { ProductsIntuitiveOutcomeService } from '../products/products-intuitive-outcome.service';
import { ProductsResultsService } from '../products/products-results.service';
import { SavedSearchCommonDataService } from './saved-search-common-data.service';

@Injectable({
  providedIn: 'root',
})
export class SavedSearchesService {
  private readonly dataService = inject(ClubHubDataService);
  private readonly router = inject(Router);
  private readonly igniteService = inject(IgniteService);
  private readonly affordabilityService = inject(AffordabilityService);
  private readonly productsSearchService = inject(ProductsSearchService);
  private readonly productsFilterService = inject(ProductsFilterService);
  private readonly productsResultSettingsService = inject(ProductsResultSettingsService);
  private readonly productsFilterHelper = inject(ProductsFilterHelper);
  private readonly productsIntuitiveOutcomeService = inject(ProductsIntuitiveOutcomeService);
  private readonly productsCalculationService = inject(ProductsCalculationService);
  private readonly productsResultsService = inject(ProductsResultsService);
  private readonly propertySearchService = inject(PropertySearchService);
  private readonly criteriaV2Service = inject(CriteriaV2Service);
  private readonly criteriaNavigateService = inject(CriteriaNavigateService);
  private readonly igniteBtlService = inject(IgniteBtlService);
  private readonly igniteCommonDataService = inject(IgniteCommonDataService);
  private readonly resultsFilterService = inject(ResultsFilterService);
  private readonly toastService = inject(ToastService);
  private readonly savedSearchCommonDataService = inject(SavedSearchCommonDataService);
  private readonly lendingTypeService = inject(LendingTypeService);

  public async saveSearchDetails(searchName: string): Promise<SavedSearchResponse> {
    // This explicit check is required because igniteService.model will just use the default
    // affordability model even if there are no affordability inputs.
    const shouldSaveAffordability = this.igniteCommonDataService.searchToolsInUse.affordability;

    const manualCriteria = this.criteriaV2Service.basketItems?.filter(c => c.criteriaType !== CriteriaType.Intuitive);
    const productFilters = this.productsResultSettingsService.getFilters();

    const model: SavedSearchRequest = {
      searchName,
      lendingTypeId: this.lendingTypeService.lendingType?.id ?? -1,
      affordabilityInputs: shouldSaveAffordability
        ? (this.igniteService.model ?? undefined)
        : undefined,
      productInputs: this.productsSearchService.requestForSaving(),
      propertyInputs: this.propertySearchService.propertySearchRequestViewModel ?? undefined,
      criteriaInputs: manualCriteria?.length ? manualCriteria : undefined,
      filters: {
        productFilters: [
          ...(Array.isArray(productFilters) ? productFilters : []),
          ...this.productsFilterService.currentAppliedFilters,
        ],
        productSort: this.productsResultSettingsService.productsSort,
        productColumns: this.productsResultsService
          .getColumnsForLendingType(this.igniteService.lendingTypeCode)
          .reduce<string[]>((acc, col) => {
            if (!col.hidden) {
              acc.push(col.name);
            }
            return acc;
          }, []),
        affordabilityFiltersAndSort: this.resultsFilterService.filters,
        quickFilters: this.productsFilterService.getSelectedFilterNames({ products: [] }),
        feeSettings: this.productsSearchService.customProductCalculations,
      },
      activeTab: this.igniteCommonDataService.source ?? undefined,
    };

    const res = await firstValueFrom(this.dataService.post<SavedSearchResponse>('SavedSearches', model));

    this.savedSearchCommonDataService.currentSearchId = res.searchId;
    this.savedSearchCommonDataService.unsavedChanges = false;

    this.toastService.success('Search saved successfully');

    return res;
  }

  public getSavedSearches() {
    return this.dataService.get<SavedSearchOverviewViewModel[]>('SavedSearches');
  }

  private getSavedSearch(searchId: number): Observable<SavedSearchRerunViewModel> {
    return this.dataService.get<SavedSearchRerunViewModel>(`SavedSearches/${searchId}`);
  }

  // Loads all saved search models and parses the JSON inputs for affordability, products, property and criteria.
  public async loadSavedSearchModels(searchId: number) {
    const search = await firstValueFrom(this.getSavedSearch(searchId));
    const activeTab = search.activeTab ?? SearchType.Criteria;

    const productInputs = search.productInputsJson ? JSON.parse(search.productInputsJson) : null;
    const affordabilityInputs = search.affordabilityInputsJson ? JSON.parse(search.affordabilityInputsJson) : null;
    const propertyInputs = search.propertyInputsJson ? JSON.parse(search.propertyInputsJson) : null;
    const criteriaInputs = search.criteriaInputsJson ? JSON.parse(search.criteriaInputsJson) : null;

    // Restore the property details from the saved search so that when you rerun a search,
    // all features (such as ESIS/EOR modal) have the same information as if you had just done a new search
    if (propertyInputs) {
      this.igniteCommonDataService.propertyModel = {
        propertyDetails: {
          ...propertyInputs,
          manualAddress: propertyInputs['manualAddress'] ?? propertyInputs.address,
        },
      } as any;
    }

    const filterInputs = search.filtersJson ? JSON.parse(search.filtersJson) : null;
    const normalizedFilterInputs = Array.isArray(filterInputs)
      ? { productFilters: filterInputs }
      : filterInputs;
    const savedFilters = !filterInputs
      ? {}
      : normalizedFilterInputs;

    this.igniteService.reset();
    this.igniteCommonDataService.reset();
    this.productsSearchService.resetProducts();
    this.productsIntuitiveOutcomeService.criteriaLoadingInProgress = false;
    this.productsIntuitiveOutcomeService.criteriaSearchId = null;
    this.igniteCommonDataService.source = activeTab;
    this.criteriaV2Service.reset();
    this.propertySearchService.reset();

    if (!this.lendingTypeService.lendingTypes.length) {
      await firstValueFrom(this.lendingTypeService.getLendingTypes());
    }

    this.lendingTypeService.setLendingTypeByCode(search.lendingTypeCode.toLowerCase());

    // The product filter definitions are lending type aware (resi/BTL) and are built from the
    // product request, so they can only be rebuilt once the lending type of the saved search is known.
    // Building them before this point used the *previous* search's lending type.
    this.productsFilterService.productsFilterContext = productInputs ?? undefined;
    this.productsFilterService.setProductFilterDefinitions();

    let affordabilityUiModel: IUiAffordabilityModel | null = null;
    if (affordabilityInputs) {
      const affordabilityUiInput = affordabilityInputs as IUiAffordabilityModel;
      const propertyAndLoan = affordabilityUiInput.propertyAndLoan;

      if (propertyAndLoan) {
        affordabilityUiInput.propertyAndLoan = {
          ...propertyAndLoan,
          amountTransferredFromOtherLender: this.convertToNumber(propertyAndLoan.amountTransferredFromOtherLender),
          interestOnlyAmount: this.convertToNumber(propertyAndLoan.interestOnlyAmount),
          equityLoanValue: this.convertToNumber(propertyAndLoan.equityLoanValue) ?? null,
          numberOfProperties: this.convertToNumber(propertyAndLoan.numberOfProperties),
          numberOfBedrooms: this.convertToNumber(propertyAndLoan.numberOfBedrooms),
          numberOfBlocks: this.convertToNumber(propertyAndLoan.numberOfBlocks),
        };
      }

      const isDefaultAffordabilityModel = JSON.stringify(affordabilityUiInput) === JSON.stringify(this.igniteService.initialModel());

      if (!isDefaultAffordabilityModel) {
        this.igniteService.setIgniteModel(affordabilityUiInput);
        this.affordabilityService.affordabilityModelRequest = this.igniteService.mappedModel(affordabilityUiInput);
        affordabilityUiModel = this.igniteService.model ?? null;
      }
    }

    // A BTL product search derives its affordability model from the product form
    // (ProductsAffordabilitySearchBtlService.prepareIgniteForm). Product filter definitions key off this flag -
    // the "Initial product period" group is disabled when an affordability model exists that did NOT come from
    // the product form - so it has to be restored, otherwise that filter comes back disabled on a rerun.
    this.igniteCommonDataService.isAffordabilityModelFromProductModel =
      !!affordabilityUiModel && activeTab === SearchType.Product;

    if (propertyInputs) {
      const propertyDetails = {
        ...this.propertySearchService.model?.propertyDetails,
        ...propertyInputs,
        manualAddress: propertyInputs['manualAddress'] ?? propertyInputs.address,
      };
      this.propertySearchService.propertySearchRequestViewModel = propertyDetails;
      this.propertySearchService.propertyModelRequest = propertyDetails;
      this.propertySearchService.updateModel({
        propertyDetails: propertyDetails,
      });
      this.propertySearchService.showAddress = true;
      this.propertySearchService.postcodeLookup = !!propertyDetails['addressId'];
    }

    if (criteriaInputs && activeTab !== SearchType.Property) {
      this.criteriaV2Service.basketItems = criteriaInputs;
    }

    // Load filters
    if (savedFilters.productSort) {
      this.productsResultSettingsService.setSort(savedFilters.productSort);
    }

    if (savedFilters.affordabilityFiltersAndSort) {
      this.resultsFilterService.filter = { ...savedFilters.affordabilityFiltersAndSort };
    }

    this.productsSearchService.ercRateReferenceDate = search.searchDate ? new Date(search.searchDate) : new Date();
    this.savedSearchCommonDataService.currentSearchId = search.searchId;
    this.savedSearchCommonDataService.unsavedChanges = false;
    this.savedSearchCommonDataService.hasStandaloneEorForCurrentSearch = search.hasStandaloneEor;
    this.savedSearchCommonDataService.standaloneEorRevision = search.hasStandaloneEor
      ? this.savedSearchCommonDataService.searchRevision
      : undefined;

    const productRequest = productInputs
      ? {
        ...productInputs,
        // A rerun is a brand new product search, so it needs a brand new id. Re-posting the saved
        // productsSearchId inserts another MiProducts row against the same id, and the MI/outcome-history
        // reads for a products search use SingleOrDefaultAsync, which then throws.
        productsSearchId: newGuid(),
      }
      : null;

    if (productRequest) {
      this.productsSearchService.reset();
      this.productsSearchService.productsSearchId = productRequest.productsSearchId;
      this.productsSearchService.productsModelRequest = productRequest;
      const { mortgageRequirements, bridging } = this.productsSearchService.getUiModelFromRequestModel(productRequest);
      this.productsSearchService.reset({ mortgageRequirements, bridging, productsSearchId: productRequest.productsSearchId });
    }

    if (savedFilters.feeSettings) {
      this.productsSearchService.customProductCalculations = savedFilters.feeSettings;
    }
    this.productsSearchService.preserveCustomProductCalculations = !!savedFilters.feeSettings;

    return {
      search,
      activeTab,
      productRequest,
      affordabilityUiModel,
      propertyRequest: propertyInputs,
      criteriaInputs,
      savedFilters,
    };
  }

  // Re-run saved search based on the specific area loaded (affordability, product, criteria, property).
  public async reRunSavedSearch(searchId: number) {
    const context = await this.loadSavedSearchModels(searchId);
    const { lendingTypeCode, lendingTypeId } = context.search;

    this.lendingTypeService.setLendingTypeByCode(lendingTypeCode);

    // The criteria rerun waits on affordability, so switch to results now or the question set shows.
    const isCriteriaRerun = context.activeTab === SearchType.Criteria && !!(context.criteriaInputs || context.propertyRequest);
    if (isCriteriaRerun) {
      this.criteriaV2Service.outcome = { details: [] } as any as OutcomeDetails;
      this.criteriaV2Service.step = CriteriaSteps.CriteriaResult;
      this.criteriaV2Service.uiLoading = true;
      this.setResultsStep(SearchType.Criteria, ResultsView.Summary);
    }

    await this.navigateToResults(lendingTypeCode, context.activeTab);

    const showProductCombinedLoading = context.activeTab === SearchType.Product && !!context.affordabilityUiModel;
    if (showProductCombinedLoading) {
      this.productsSearchService.currentStepName = StepName.Results;
      this.productsSearchService.resultsView = ResultsView.Details;
      this.igniteCommonDataService.resultsView = ResultsView.MatchedProducts;
      this.igniteCommonDataService.source = SearchType.Product;
    }

    let pendingAffordability: Promise<void> | null = null;

    try {
      if (context.affordabilityUiModel) {
        const isAffordabilityTab = context.activeTab === SearchType.Affordability;
        const isCriteriaTab = context.activeTab === SearchType.Criteria;
        if (!isAffordabilityTab && showProductCombinedLoading) {
          this.setProductsLoadingPhase(SearchType.Product);
        }

        // Whichever step owns products/criteria for this tab must be the only one fetching them. They all
        // write to the same shared state (productsSearchService.productsModelRequest / matchedProducts and
        // criteriaV2Service.outcome), so overlapping fetches are a race where the last response wins.
        const affordabilityPromise = this.processAffordabilityRerun(
          context.affordabilityUiModel,
          {
            preserveActiveTab: !isAffordabilityTab,
            skipProductReload: !!context.productRequest && (context.activeTab === SearchType.Product || isCriteriaTab),
            skipCriteriaReload: isCriteriaTab,
            feeSettings: context.savedFilters?.feeSettings,
          },
        );

        // The criteria rerun needs the affordabilitySearchId that the affordability run produces, so it
        // cannot start until affordability has finished.
        if (isAffordabilityTab || isCriteriaTab) {
          await affordabilityPromise;
        } else {
          pendingAffordability = affordabilityPromise;
          // Mark it handled so a failure in a later step below doesn't surface as an unhandled rejection.
          // The rejection is still raised where it is awaited.
          affordabilityPromise.catch(() => undefined);
        }
      }

      if (context.activeTab === SearchType.Property && context.propertyRequest) {
        await this.processPropertyRerun(context.propertyRequest);
      }

      if (isCriteriaRerun) {
        this.igniteCommonDataService.source = SearchType.Criteria;
        await this.processCriteriaRerun(
          lendingTypeId,
          context.criteriaInputs,
          context.propertyRequest,
          context.productRequest,
          context.savedFilters,
        );
      }

      if (context.activeTab === SearchType.Product && context.productRequest) {
        if (showProductCombinedLoading) {
          this.setProductsLoadingPhase(SearchType.Product);
        }
        await this.processProductRerun(
          lendingTypeCode,
          context.productRequest,
          context.savedFilters,
          context.propertyRequest,
          !!context.affordabilityUiModel,
        );
      }

      // For affordability and product reruns that include property data, this won't
      // populate the property state. This means that the property button in the top-bar
      // shows "Add" instead of "Edit" and the user won't be able to edit property info.
      if (context.activeTab !== SearchType.Property && context.activeTab !== SearchType.Criteria && context.propertyRequest) {
        if (showProductCombinedLoading) {
          this.setProductsLoadingPhase(SearchType.Product);
        }
        this.propertySearchService.propertySearchRequestViewModel = context.propertyRequest;
        this.propertySearchService.propertyModelRequest = context.propertyRequest;
        this.propertySearchService.updateModel({
          propertyDetails: {
            ...this.propertySearchService.model?.propertyDetails,
            ...(context.propertyRequest as unknown as Record<string, unknown>),
          },
        });

        const propertyResult = await firstValueFrom(
          this.dataService.post<PropertyOutcomeDetailsDetailed>('propertyCriteriaV2', context.propertyRequest),
        );

        this.propertySearchService.propertySearchId = propertyResult.propertySearchId;
        this.propertySearchService.propertySearchId = propertyResult.propertySearchId;
        this.propertySearchService.autoAddedPropertyOutcomeIds = propertyResult.autoAddedOutcomeIds ?? [];
        this.propertySearchService.setPropertyResults(propertyResult, propertyResult.propertySearchId, true);
        this.igniteCommonDataService.searchToolsInUse.property = true;
      }

      // Affordability also loads the criteria outcome, so the rerun is not finished until it settles.
      // Leaving it detached cleared the loading state while results were still arriving and turned any
      // failure into an unhandled rejection instead of the error toast below.
      if (pendingAffordability) {
        await pendingAffordability;
      }

      if (context.activeTab !== SearchType.Affordability) {
        this.igniteCommonDataService.source = context.activeTab;
      }

      this.savedSearchCommonDataService.currentSearchId = context.search.searchId;
      this.savedSearchCommonDataService.unsavedChanges = false;
      this.savedSearchCommonDataService.hasStandaloneEorForCurrentSearch = !!context.search.hasStandaloneEor;
      this.savedSearchCommonDataService.standaloneEorRevision = context.search.hasStandaloneEor
        ? this.savedSearchCommonDataService.searchRevision
        : undefined;
    } catch (error) {
      this.toastService.danger('An error occurred while re-running your saved search');
      throw error;
    } finally {
      if (showProductCombinedLoading) {
        this.setProductsLoadingPhase(null);
      }
      if (isCriteriaRerun) {
        this.criteriaV2Service.uiLoading = false;
      }
    }
  }

  private async navigateToResults(lendingTypeCode: string, activeTab: SearchType) {
    switch (activeTab) {
      case SearchType.Criteria:
        this.criteriaNavigateService.navigate('issues', {
          queryParams: { rerunSavedSearch: true },
        });
        return;
      case SearchType.Affordability:
        await this.router.navigate(['/ignite/affordability/form', lendingTypeCode.toLowerCase()], {
          queryParams: { rerunSavedSearch: true },
        });
        break;
      case SearchType.Property:
        await this.router.navigate(['/ignite/property/form', lendingTypeCode.toLowerCase()], {
          queryParams: { rerunSavedSearch: true },
        });
        break;
      case SearchType.Product:
      default:
        await this.router.navigate(['/ignite/products/form', lendingTypeCode.toLowerCase()], {
          queryParams: { rerunSavedSearch: true },
        });
        break;
    }
  }

  private async processProductRerun(
    lendingTypeCode: string,
    productRequest: ProductSavedRequest,
    savedFilters: SavedFilters,
    propertyRequest: PropertyModelRequest | null = null,
    skipIntuitiveCriteria = false,
  ) {
    const savedFeeSettings = savedFilters?.feeSettings ?? this.productsSearchService.customProductCalculations;
    const savedFilterState = this.getSavedProductFilterState(lendingTypeCode, savedFilters?.productFilters, savedFilters?.quickFilters);

    this.productsSearchService.resultsView = ResultsView.Details;
    this.productsSearchService.currentStepName = StepName.Results;
    if (this.igniteCommonDataService.source === SearchType.Product) {
      this.setResultsStep(SearchType.Product, ResultsView.MatchedProducts);
    }
    this.igniteCommonDataService.searchToolsInUse.products = true;
    const showProductUiLoading = this.igniteCommonDataService.source === SearchType.Product;
    if (showProductUiLoading) {
      this.productsSearchService.loading = true;
    }
    this.productsSearchService.productLoadingInProgress = true;

    if (savedFilterState.gridFilters.length) {
      this.productsResultSettingsService.clearFilters();
      savedFilterState.gridFilters.forEach(filter => this.productsResultSettingsService.addFilter(filter));
    }

    this.productsSearchService.isReplayingSavedSearch = true;

    try {
      // Fetch products and criteria at the same time
      const getProductsPromise = lastValueFrom(this.productsSearchService.getProducts(false, productRequest, savedFeeSettings));
      const intuitivePropertyRequest = propertyRequest ?? this.propertySearchService.propertySearchRequestViewModel;
      const getCriteriaPromise = !skipIntuitiveCriteria && this.productsSearchService.shouldLoadIntuitiveCriteria
        ? firstValueFrom(this.productsIntuitiveOutcomeService.getIntuitiveOutcome(productRequest, intuitivePropertyRequest))
          .then(() => {
            this.igniteCommonDataService.searchToolsInUse.criteria = true;
          })
          .catch(() => {
            this.toastService.danger('Criteria could not be loaded for this search');
          })
        : Promise.resolve();

      await Promise.all([getProductsPromise, getCriteriaPromise]);

      // Fees and true cost
      if (savedFeeSettings) {
        this.productsSearchService.customProductCalculations = savedFeeSettings;
        const restoredProducts = this.productsSearchService.matchedProductsFlat ?? [];
        restoredProducts.forEach(product => {
          product.brokerFees = this.productsSearchService.getBrokerFees(product);
          product.assumedLegalFees = this.productsSearchService.getAssumedLegalFees(product);
        });
        this.productsCalculationService.performCalculations(
          restoredProducts,
          productRequest,
          savedFeeSettings.deductCashback,
          savedFeeSettings.customFeeCalculationScenarios,
          this.productsSearchService.getCustomFees(),
          savedFeeSettings.assumedLegalFeeCost,
          false,
          savedFeeSettings.customTrueCostPeriod,
          this.productsSearchService.ercRateReferenceDate,
        );
      }

      // Filters
      if (savedFilterState.quickFilters.length) {
        this.productsFilterService.applySavedFilters(savedFilterState.quickFilters);
      }

      if (savedFilterState.quickFilterSelections.length) {
        this.productsFilterService.setFilters(savedFilterState.quickFilterSelections);
      }

      // The filters now show the saved state for this request, which is what a later save should start from.
      this.productsSearchService.requestShownInFilters = this.productsSearchService.productsModelRequest ?? null;

      if (savedFilterState.gridFilters.length && this.productsSearchService.productsGrid) {
        savedFilterState.gridFilters.forEach(filter => this.productsSearchService.productsGrid?.setFilterRaw(filter));
      }

      if (savedFilters?.productSort?.sortOn) {
        const { productSort } = savedFilters;
        this.productsResultSettingsService.setSort(productSort);
        this.productsSearchService.productsGrid?.setSort(productSort.sortOn, productSort.sortDir);
      }
    } finally {
      if (showProductUiLoading) {
        this.productsSearchService.loading = false;
      }
      this.productsSearchService.productLoadingInProgress = false;
      this.productsSearchService.isReplayingSavedSearch = false;
    }
  }

  private async processAffordabilityRerun(
    affordabilityUiModel: IUiAffordabilityModel,
    options?: {
      preserveActiveTab?: boolean;
      skipProductReload?: boolean;
      skipCriteriaReload?: boolean;
      feeSettings?: CustomProductCalculations;
    },
  ) {
    const savedAffordabilityFilters = { ...this.resultsFilterService.filter };

    this.igniteService.setIgniteModel(affordabilityUiModel);
    this.affordabilityService.affordabilityModelRequest = this.igniteService.mappedModel(affordabilityUiModel);
    const affordabilityRequest = this.affordabilityService.affordabilityModelRequest;
    if (!affordabilityRequest) {
      throw new Error('Unable to map saved affordability UI model to request');
    }

    const requestPayload: AffordabilityAndUiRequest = {
      affordabilityRequest,
      uiModelJson: JSON.stringify(affordabilityUiModel),
    };

    const isBtlAffordability = String(affordabilityRequest.productType ?? '').toUpperCase() === LendingTypeCode.Btl;

    if (!options?.preserveActiveTab) {
      this.setResultsStep(SearchType.Affordability, ResultsView.Details);
    }
    this.affordabilityService.loading = true;
    this.criteriaV2Service.loading = true;
    this.igniteService.progress = 0;
    this.affordabilityService.affordabilityLoading$.next(true);

    try {
      sessionStorage.setItem(keys.flowType, FlowType.User.toString());
      const affordabilityResults =
        isBtlAffordability
          ? await lastValueFrom(this.igniteBtlService.getAffordability(requestPayload))
          : await lastValueFrom(this.dataService.post<AffordabilityPollResponse>('Affordability', requestPayload));

      this.igniteCommonDataService.results = affordabilityResults;
      this.igniteCommonDataService.lenders = affordabilityResults.lenders ?? [];
      this.igniteService.lenderCount = affordabilityResults.lenderCount;

      let finalAffordabilityResults = affordabilityResults;

      if (affordabilityResults.pollJobID && affordabilityResults.affordabilitySearchId) {
        finalAffordabilityResults = await this.pollAffordability(
          affordabilityResults.pollJobID,
          affordabilityResults.affordabilitySearchId,
        );
      }

      const affordabilitySearchId = finalAffordabilityResults.affordabilitySearchId ?? affordabilityResults.affordabilitySearchId;
      if (affordabilitySearchId) {
        const productsRequest = this.mapAffordabilityToProductRequest(affordabilityRequest, affordabilitySearchId);
        const lendingTypeId = this.lendingTypeService.lendingType?.id;

        // Reload products and criteria at the same time. Prevents the loading taking longer than it should.
        const reloadProducts = async () => {
          if (!productsRequest || options?.skipProductReload) {
            return;
          }

          this.productsSearchService.loading = true;
          this.productsSearchService.productLoadingInProgress = true;
          this.productsSearchService.productsModelRequest = productsRequest;

          this.productsSearchService.isReplayingSavedSearch = true;

          try {
            await lastValueFrom(this.productsSearchService.getProducts(
              false, productsRequest, options?.feeSettings ?? this.productsSearchService.customProductCalculations,
            ));
            this.productsSearchService.updateModelFromSF(productsRequest);
            this.igniteCommonDataService.searchToolsInUse.products = true;
          } finally {
            this.productsSearchService.loading = false;
            this.productsSearchService.productLoadingInProgress = false;
            this.productsSearchService.isReplayingSavedSearch = false;
          }
        };

        // Intuitive criteria are derived server side from the affordability request, so this call has to be
        // made even when there are no manual criteria in the basket. Only manual criteria are persisted with a
        // saved search, so bailing out on an empty basket meant a rerun of any search whose criteria were
        // purely intuitive (every BTL product search, which runs affordability off the product form) came back
        // with an empty outcome and rendered "-" for every lender until the broker re-ran the search by hand.
        const reloadCriteria = async () => {
          if (!lendingTypeId || options?.skipCriteriaReload) {
            return;
          }

          this.criteriaV2Service.loading = true;

          try {
            this.criteriaV2Service.criteriaIssues = await firstValueFrom(this.criteriaV2Service.getIssues(lendingTypeId));
            await lastValueFrom(
              this.criteriaV2Service.getOutcome(
                lendingTypeId,
                affordabilitySearchId,
                this.propertySearchService.propertySearchRequestViewModel,
                affordabilityRequest,
              ),
            );

            this.igniteCommonDataService.searchToolsInUse.criteria = true;
          } finally {
            this.criteriaV2Service.loading = false;
          }
        };

        await Promise.all([reloadProducts(), reloadCriteria()]);
      }

      this.resultsFilterService.filter = { ...savedAffordabilityFilters };
      this.igniteCommonDataService.searchToolsInUse.affordability = true;
    } finally {
      this.affordabilityService.affordabilityLoading$.next(false);
      this.affordabilityService.loading = false;
      this.criteriaV2Service.loading = false;
    }
  }

  private async processPropertyRerun(propertyRequest: PropertyModelRequest) {
    this.propertySearchService.resultsView = ResultsView.Details;
    this.propertySearchService.currentStepName = StepName.Results;
    this.propertySearchService.propertySearchRequestViewModel = propertyRequest;
    this.setResultsStep(SearchType.Property, ResultsView.Details);
    this.criteriaV2Service.loading = true;
    this.criteriaV2Service.clearBasketProperty();
    this.criteriaV2Service.clearBasketIntuitive();
    this.criteriaV2Service.removeBasketItemByContextName('EPC Rating');

    try {
      const propertyResult = await firstValueFrom(
        this.dataService.post<PropertyOutcomeDetailsDetailed>('propertyCriteriaV2', propertyRequest),
      );

      this.propertySearchService.propertySearchId = propertyResult.propertySearchId;
      this.propertySearchService.propertySearchId = propertyResult.propertySearchId;
      this.propertySearchService.autoAddedPropertyOutcomeIds = propertyResult.autoAddedOutcomeIds ?? [];
      this.propertySearchService.setPropertyResults(
        propertyResult,
        propertyResult.propertySearchId,
        !this.igniteCommonDataService.propertyOnly,
      );
      this.igniteCommonDataService.resultsView = ResultsView.Summary;
      this.igniteCommonDataService.lenders = propertyResult.details.map(x => ({
        lenderName: x.overallResult.lender,
        lenderLogoUrl: x.overallResult.lenderLogoUrl,
      }));
      this.igniteCommonDataService.searchToolsInUse.property = true;
    } finally {
      this.criteriaV2Service.loading = false;
    }
  }

  private async processCriteriaRerun(
    lendingTypeId: number,
    criteriaInputs: CriteriaFormResult[] | null,
    propertyRequest: PropertyModelRequest | null,
    productRequest?: ProductSavedRequest | null,
    savedFilters?: SavedFilters | null,
  ) {
    this.criteriaV2Service.outcome = { details: [] } as any as OutcomeDetails;
    this.criteriaV2Service.step = CriteriaSteps.CriteriaResult;
    this.criteriaV2Service.dataLoading = true;
    this.criteriaV2Service.uiLoading = true;
    this.setResultsStep(SearchType.Criteria, ResultsView.Summary);
    this.igniteCommonDataService.searchToolsInUse.criteria = true;

    try {
      if (propertyRequest) {
        this.propertySearchService.propertySearchRequestViewModel = propertyRequest;

        const propertyResult = await firstValueFrom(
          this.dataService.post<PropertyOutcomeDetailsDetailed>('propertyCriteriaV2', propertyRequest),
        );
        this.propertySearchService.propertyModelRequest = propertyRequest;
        this.propertySearchService.propertySearchId = propertyResult.propertySearchId;
        this.propertySearchService.updateModel({
          propertyDetails: {
            ...this.propertySearchService.model?.propertyDetails,
            ...propertyRequest,
          },
        });
        this.propertySearchService.propertySearchId = propertyResult.propertySearchId;
        this.propertySearchService.autoAddedPropertyOutcomeIds = propertyResult.autoAddedOutcomeIds ?? [];
        this.propertySearchService.setPropertyResults(propertyResult, propertyResult.propertySearchId, true);
        this.igniteCommonDataService.searchToolsInUse.property = true;
      }

      const newIssues = await firstValueFrom(this.criteriaV2Service.getIssues(lendingTypeId));
      this.criteriaV2Service.criteriaIssues = newIssues;

      // The manual outcome has to be fetched before the product rerun. getOutcome() *replaces*
      // criteriaV2Service.outcome, whereas the product intuitive outcome *merges* into it. Running products
      // first therefore wiped the intuitive criteria as soon as the manual outcome came back.
      if (criteriaInputs?.length) {
        await lastValueFrom(
          this.criteriaV2Service.getOutcome(
            lendingTypeId,
            this.affordabilityService.affordabilityModelRequest?.affordabilitySearchId ?? null,
            this.propertySearchService.propertySearchRequestViewModel,
            this.affordabilityService.affordabilityModelRequest,
          ),
        );
      }

      if (productRequest) {
        await this.processProductRerun(
          this.igniteService.lendingTypeCode.toLowerCase(),
          productRequest,
          savedFilters ?? ({} as SavedFilters),
          this.propertySearchService.propertySearchRequestViewModel ?? null,
          false,
        );
      }
    } finally {
      this.criteriaV2Service.dataLoading = false;
      this.criteriaV2Service.uiLoading = false;
    }
  }

  // Creates a filter state for the different filter types.
  private getSavedProductFilterState(
    lendingTypeCode: string,
    productFilters: SavedFilters['productFilters'],
    savedQuickFilters: Record<string, any> | null | undefined,
  ) {
    const state: SavedProductFilterState = {
      gridFilters: [],
      quickFilters: [],
      quickFilterSelections: [],
    };

    // Split saved filters into grid filters and quick filters.
    const filters = Array.isArray(productFilters) ? productFilters : [];
    for (const filter of filters) {
      if (!filter || typeof filter !== 'object') {
        continue;
      }

      if ('column' in filter && 'op' in filter && 'value' in filter) {
        state.gridFilters.push(filter);
        continue;
      }

      if ('filterGroup' in filter && 'filterName' in filter) {
        state.quickFilters.push(filter as MiProductsFiltersViewModel);
      }
    }

    if (state.quickFilters.length) {
      return state;
    }

    if (!savedQuickFilters) {
      return state;
    }

    // Determine which filter definitions to use (bridging vs resi/BTL)
    const isBridgingType = lendingTypeCode === LendingTypeCode.Bdg.toLowerCase();
    const productFilterDefinitions = isBridgingType
      ? this.productsFilterHelper.bridgingProductFilterDefinitions
      : this.productsFilterHelper.resAndBtlProductFilterDefinitions(lendingTypeCode === LendingTypeCode.Btl.toLowerCase());

    // Extract any saved range filter values (for custom ranges such as initial product period)
    const savedRangeFilters = Array.isArray(savedQuickFilters['rangeFilters']) ? savedQuickFilters['rangeFilters'] : [];

    Object.entries(savedQuickFilters).forEach(([requestObjKey, values]) => {
      if (requestObjKey === 'rangeFilters' || requestObjKey.endsWith('Included') || requestObjKey.endsWith('Excluded') || !Array.isArray(values)) {
        return;
      }

      const group = productFilterDefinitions.find(g => g.requestObjKey === requestObjKey);
      if (!group) {
        return;
      }

      values.forEach(value => {
        if (typeof value !== 'string' || !value.length) {
          return;
        }

        const savedRangeFilter = savedRangeFilters.find(filter => filter?.name === value);
        if (savedRangeFilter) {
          state.quickFilters.push({
            filterGroup: group.title,
            filterName: value,
            isChecked: true,
            isIncluded: false,
            isExcluded: false,
            checkedSubFilters: [],
            hasRangeValue: true,
            fromValue: savedRangeFilter.from,
            toValue: savedRangeFilter.to,
          });
          return;
        }

        // Treat as a regular filter if there is no range present
        state.quickFilterSelections.push({ groupKey: group.title, filterKey: value, checked: true });
      });
    });

    return state;
  }

  private async pollAffordability(pollJobID: number, affordabilitySearchId: string): Promise<AffordabilityPollResponse> {
    const MAX_POLLS = 60;
    let polls = 0;

    while (polls++ < MAX_POLLS) {
      const pollResult = await firstValueFrom(
        this.igniteService.getAffordabilityPoll(pollJobID, affordabilitySearchId),
      );
      this.igniteService.progress = pollResult.quotes?.length ?? 0;
      this.igniteCommonDataService.results = pollResult;

      if (pollResult.completed) {
        return pollResult;
      }

      await firstValueFrom(timer(3000));
    }

    throw new Error('Affordability poll timed out');
  }

  private mapAffordabilityToProductRequest(
    request: AffordabilityRequest | BtlAffordabilityRequest | BdgAffordabilityRequest,
    affordabilitySearchId: string,
  ) {
    if (request.productType === LendingTypeCode.Res) {
      return ProductsResidentialModelRequest.mapAffordabilityToProductRequestResidential(
        request as AffordabilityRequest,
        affordabilitySearchId,
        null,
      );
    }

    if (request.productType === LendingTypeCode.Btl) {
      return ProductsBtlModelRequest.mapAffordabilityToProductRequestBtl(
        request as BtlAffordabilityRequest,
        affordabilitySearchId,
        null,
      );
    }

    if (request.productType === LendingTypeCode.Bdg) {
      return ProductsBdgModelRequest.mapAffordabilityToProductRequestBdg(request as BdgAffordabilityRequest, affordabilitySearchId);
    }
  }

  private setProductsLoadingPhase(phase: SearchType.Criteria | SearchType.Product | null) {
    this.criteriaV2Service.loading = phase === SearchType.Criteria;
    this.productsSearchService.loading = phase === SearchType.Product;
    this.productsSearchService.productLoadingInProgress = phase === SearchType.Product;
    this.productsIntuitiveOutcomeService.criteriaLoadingInProgress = phase === SearchType.Criteria;
  }

  private setResultsStep(source: SearchType, view: ResultsView) {
    this.igniteService.currentStepName = StepName.Results;
    this.igniteCommonDataService.source = source;
    this.igniteCommonDataService.resultsView = view;
  }

  public async editSavedSearchProduct(searchId: number) {
    const { affordabilityUiModel, search, savedFilters } = await this.loadSavedSearchModels(searchId);
    this.restoreSavedProductFilters(search.lendingTypeCode, savedFilters);

    // If the user is editing a resi products, their original search may have been done automatically by them completing the affordability
    // form. If this is the case, we need to take the user back to the affordability form instead of the product form.
    if (search.lendingTypeCode === LendingTypeCode.Res && affordabilityUiModel) {
      this.goToAffordabilityForm(search.lendingTypeCode);
    } else {
      this.igniteService.caseEditedAll = true;
      this.productsSearchService.currentStepName = StepName.MortgageRequirements;
      this.criteriaV2Service.clearBasketIntuitive();
      this.router.navigate(['/ignite/products/form', search.lendingTypeCode.toLowerCase()], {
        queryParams: { rerunSavedSearch: true },
      });
    }
  }

  public async editSavedSearchAffordability(searchId: number) {
    const { search, savedFilters } = await this.loadSavedSearchModels(searchId);
    this.restoreSavedProductFilters(search.lendingTypeCode, savedFilters);
    this.goToAffordabilityForm(search.lendingTypeCode);
  }

  /**
   * Puts the saved search's sidebar filters back before the broker edits its inputs.
   *
   * Re-run restores these after its replay; Edit inputs never did, so the search the broker then ran from the form
   * came back without them - Product features and anything else in the sidebar. Restored up front here rather than
   * after a search, because rebuilding the filter definitions on that search now keeps the selections it finds.
   * Rate type and Initial product period are re-set from the form when the search runs, so an edit to either on
   * the form still wins.
   *
   * Grid column filters are deliberately left out. Re-run applies those to the grid itself once its replay
   * finishes, and nothing does that after a search from the form - restoring them here would put them in the
   * saved state without ever showing them, and the next save would store filters the broker could not see.
   */
  private restoreSavedProductFilters(lendingTypeCode: string, savedFilters: SavedFilters) {
    const savedFilterState = this.getSavedProductFilterState(lendingTypeCode, savedFilters?.productFilters, savedFilters?.quickFilters);

    if (savedFilterState.quickFilters.length) {
      this.productsFilterService.applySavedFilters(savedFilterState.quickFilters);
    }

    if (savedFilterState.quickFilterSelections.length) {
      this.productsFilterService.setFilters(savedFilterState.quickFilterSelections);
    }
  }

  public async editSavedSearchCriteria(searchId: number) {
    await this.loadSavedSearchModels(searchId);
    this.criteriaNavigateService.navigate('issues', {
      queryParams: { rerunSavedSearch: true },
    });
  }

  public async editSavedSearchProperty(searchId: number) {
    const { search } = await this.loadSavedSearchModels(searchId);
    this.router.navigate(['/ignite/property/form', search.lendingTypeCode.toLowerCase()], {
      queryParams: { rerunSavedSearch: true },
    });
  }

  private goToAffordabilityForm(lendingTypeCode: string) {
    this.igniteService.caseEditedAll = true;
    this.igniteService.currentStepName = StepName.PropertyAndLoan;
    this.criteriaV2Service.clearBasketIntuitive();
    this.router.navigate(['/ignite/affordability/form', lendingTypeCode.toLowerCase()], {
      queryParams: { rerunSavedSearch: true },
    });
  }

  // Some saved searches can have missing values for fields that should be numbers.
  // This ensures the values are converted to numbers to prevent any invalid data being sent to the API.
  private convertToNumber(value: any) {
    if (value === null || value === undefined || value === false || value === '') {
      return undefined;
    }

    const parsedValue = typeof value === 'string' ? Number(value.trim()) : value;
    return typeof parsedValue === 'number' && Number.isFinite(parsedValue) ? parsedValue : undefined;
  }
}
